import React from "react";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LocaleContext from "../../store/locale-context";
import FlagIcon from "./FlagIcon";

interface PickerItem {
  code: string;
  label: string;
  icon?: React.ReactNode;
  trailing?: string;
}

interface PickerProps {
  label: string;
  icon?: React.ReactNode;
  heading?: string;
  items: PickerItem[];
  selectedCode: string;
  onSelect: (code: string) => void;
  color?: string;
}

const Picker: React.FC<PickerProps> = ({ label, icon, heading, items, selectedCode, onSelect, color }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          cursor: "pointer",
          color: color || "text.secondary",
          fontSize: 14,
          fontWeight: 500,
          userSelect: "none",
          opacity: 1,
          "&:hover": { opacity: 0.8 },
        }}
      >
        {icon}
        <Box component="span">{label}</Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { maxHeight: 340, minWidth: 240, py: 0.5 } }}
      >
        {heading && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, letterSpacing: 0.5, color: "text.secondary" }}
            >
              {heading}
            </Typography>
          </Box>
        )}
        {items.map((item) => {
          const selected = item.code === selectedCode;
          return (
            <MenuItem
              key={item.code}
              selected={selected}
              onClick={() => {
                onSelect(item.code);
                setAnchorEl(null);
              }}
              sx={
                selected
                  ? {
                      bgcolor: "secondary.main",
                      color: "#fff",
                      borderRadius: 1,
                      mx: 1,
                      "&:hover": { bgcolor: "secondary.main" },
                    }
                  : { mx: 1, borderRadius: 1 }
              }
            >
              {item.icon && <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>}
              <ListItemText
                primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
              >
                {item.label}
              </ListItemText>
              {item.trailing && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    ml: 1,
                    color: selected ? "#fff" : "text.secondary",
                  }}
                >
                  {item.trailing}
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

interface TopLocaleBarProps {
  sx?: object;
  color?: string;

  reloadOnChange?: boolean;

  onCountryChange?: (code: string) => void;
}

const TopLocaleBar: React.FC<TopLocaleBarProps> = ({ sx, color, reloadOnChange, onCountryChange }) => {
  const { country, language, countries, languages, setCountry, setLanguage } =
    React.useContext(LocaleContext);

  const selectedCountry = countries.find((c) => c.code === country);
  const selectedLanguage = languages.find((l) => l.code === language);

  const handleCountrySelect = (code: string) => {
    if (code === country) return;
    setCountry(code);
    if (onCountryChange) {

      onCountryChange(code);
      return;
    }

    if (reloadOnChange) window.location.reload();
  };

  const handleLanguageSelect = (code: string) => {
    if (code === language) return;

    setLanguage(code);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 3,
        width: "100%",
        py: 1.5,
        ...sx,
      }}
    >
      <Picker
        label={selectedCountry?.name || country}
        icon={<FlagIcon countryCode={country} size={18} />}
        heading="COUNTRY / CURRENCY"
        items={countries.map((c) => ({
          code: c.code,
          label: c.name,
          icon: <FlagIcon countryCode={c.code} size={20} />,
          trailing: c.currencyCode || undefined,
        }))}
        selectedCode={country}
        onSelect={handleCountrySelect}
        color={color}
      />
      <Picker
        label={selectedLanguage?.nativeName || selectedLanguage?.name || language}
        heading="LANGUAGE"
        items={languages.map((l) => ({
          code: l.code,
          label: l.nativeName !== l.name ? `${l.nativeName} (${l.name})` : l.name,
        }))}
        selectedCode={language}
        onSelect={handleLanguageSelect}
        color={color}
      />
    </Box>
  );
};

export default TopLocaleBar;
